import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { colors, font, space, radius } from '@/theme/chalkCourt';
import { DoublePunch } from '@/utils/punchAnomalies';
import { MissingPunchTarget } from '@/components/AddMissingPunchModal';
import { SwitchTarget } from '@/components/SwitchPunchModal';

interface Props {
  double: DoublePunch;
  onSwitch: (target: SwitchTarget) => void;
  onAddMissing: (target: MissingPunchTarget) => void;
}

const at = (iso: string) => parseISO(iso);
const show = (iso: string) => format(at(iso), 'h:mm a');
const hm = (iso: string) => format(at(iso), 'HH:mm');

const fmtLength = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

/**
 * Two clock-ins (or clock-outs) in a row. Offers the two real fixes: the second
 * punch was meant to be the other direction (switch it), or a punch in between
 * was missed (add it).
 */
const DoublePunchNotice: React.FC<Props> = ({ double: d, onSwitch, onAddMissing }) => {
  const firstIso = d.first.adjustedTime || d.first.actualTime;
  const secondIso = d.second.adjustedTime || d.second.actualTime;
  const first = show(firstIso);
  const second = show(secondIso);
  const isIn = d.direction === 'in';
  const minutes = Math.round((at(secondIso).getTime() - at(firstIso).getTime()) / 60000);

  const switchTarget: SwitchTarget = {
    punchId: d.second.id,
    time: second,
    wasClockIn: isIn,
    effect: isIn
      ? `${first} – ${second} becomes a ${fmtLength(minutes)} shift.`
      : `You'll be back on the clock from ${second}.`,
  };
  const missingTarget: MissingPunchTarget = isIn
    ? { date: d.date, mode: 'out', otherTime: hm(firstIso), bound: hm(secondIso) }
    : { date: d.date, mode: 'in', otherTime: hm(secondIso), bound: hm(firstIso) };

  return (
    <View style={styles.box}>
      <View style={styles.head}>
        <Icon name="git-compare-outline" size={17} color={colors.warn} />
        <Text style={styles.title}>
          Two clock-{isIn ? 'ins' : 'outs'} in a row · {first} and {second}
        </Text>
      </View>
      <TouchableOpacity
        style={[styles.option, styles.optionPrimary]}
        activeOpacity={0.85}
        onPress={() => onSwitch(switchTarget)}>
        <Icon name="swap-horizontal" size={16} color={colors.accentDark} />
        <Text style={[styles.optionText, styles.optionTextPrimary]}>
          {second} was a clock-{isIn ? 'out' : 'in'} — switch it
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.option}
        activeOpacity={0.85}
        onPress={() => onAddMissing(missingTarget)}>
        <Icon name="add-circle-outline" size={16} color={colors.inkSoft} />
        <Text style={styles.optionText}>
          I forgot to clock {isIn ? 'out' : 'in'} in between
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  box: {
    backgroundColor: colors.warnSoft,
    borderWidth: 1,
    borderColor: '#E8D6A8',
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    gap: space.sm,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    flex: 1,
    fontFamily: font.bodyBold,
    fontSize: 14,
    color: colors.warn,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: space.md,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.surface,
  },
  optionPrimary: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  optionText: {
    flex: 1,
    fontFamily: font.bodySemi,
    fontSize: 13.5,
    color: colors.inkSoft,
  },
  optionTextPrimary: {
    color: colors.accentDark,
  },
});

export default DoublePunchNotice;
